import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Series as SeriesType, Lab, Run } from "../lib/supabase";
import { ENTITY_NAMES } from "../constants/entities";
import {
  Book,
  Tag,
  Edit,
  Eye,
  Trash2,
  Plus,
  ArrowUp,
  ArrowDown,
  Clipboard,
} from "lucide-react";
import LoadingScreen from "../components/ui/LoadingScreen";
import { useSeries } from "../contexts/SeriesContext";
import { useAuth } from "../contexts/AuthContext";
import SmartBackButton from "../components/ui/SmartBackButton";
import RunsSection from "../components/runs/RunsSection";
import toast from "react-hot-toast";

const SeriesDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [series, setSeries] = useState<SeriesType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [prerequisiteSeries, setPrerequisiteSeries] = useState<SeriesType[]>(
    []
  );
  const { refreshSeries } = useSeries();
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = userRole === "admin";

  useEffect(() => {
    const fetchSeries = async () => {
      setIsLoading(true);
      if (!id) {
        setIsLoading(false);
        return;
      }

      // Use refreshSeries to get the latest data
      const data = await refreshSeries(id);
      if (data) {
        setSeries(data);
        // Fetch prerequisite series if they exist
        if (data.prerequisites && data.prerequisites.length > 0) {
          fetchPrerequisiteSeries(data.prerequisites);
        }
      }
      setIsLoading(false);
    };
    fetchSeries();
  }, [id, refreshSeries]);

  const fetchPrerequisiteSeries = async (prerequisiteIds: string[]) => {
    try {
      const { data, error } = await supabase
        .from("series")
        .select("id, created_at, title, description, code, prerequisites")
        .in("id", prerequisiteIds)
        .order("code", { ascending: true });

      if (error) throw error;
      if (data) setPrerequisiteSeries(data);
    } catch (error) {
      console.error("Error fetching prerequisite series:", error);
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchLabs();
  }, [id]);

  const fetchLabs = async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from("labs")
      .select(
        `
        *,
        lab_resources (
          id,
          type,
          title,
          url,
          file_path,
          file_name,
          file_type
        )
      `
      )
      .eq("series_id", id)
      .order("order_number");
    if (!error && Array.isArray(data)) {
      // Transform the data to match the Lab type
      const labsWithResources = data.map((lab) => ({
        ...lab,
        resources: lab.lab_resources || [],
      }));
      setLabs(labsWithResources as Lab[]);
    }
  };

  useEffect(() => {
    if (!id) return;
    handleRunsChange();
  }, [id]);

  const handleRunsChange = async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from("runs")
      .select(
        `
        *,
        lab_dates (
          id,
          run_id,
          lab_number,
          date
        )
      `
      )
      .eq("series_id", id)
      .order("day_of_week")
      .order("start_time");

    if (error) {
      console.error("Error fetching runs:", error);
      return;
    }

    if (Array.isArray(data)) {
      setRuns(data as Run[]);
    }
  };

  const handleDeleteSeries = async () => {
    if (!series || !isAdmin || !id) return;

    if (
      window.confirm(
        `Are you sure you want to delete this ${ENTITY_NAMES.SERIES}? This will also delete all associated ${ENTITY_NAMES.RUN}s and student enrollments.`
      )
    ) {
      try {
        const { error } = await supabase.from("series").delete().eq("id", id);

        if (error) throw error;

        toast.success(`${ENTITY_NAMES.SERIES} deleted successfully`);
        navigate("/series");
      } catch (error) {
        console.error("Error deleting series:", error);
        toast.error(`Failed to delete ${ENTITY_NAMES.SERIES.toLowerCase()}`);
      }
    }
  };

  const handleDeleteLab = async (labId: string) => {
    if (!isAdmin) return;

    if (
      window.confirm(
        `Are you sure you want to delete this ${ENTITY_NAMES.LAB}?`
      )
    ) {
      try {
        const { error } = await supabase.from("labs").delete().eq("id", labId);

        if (error) throw error;

        toast.success(`${ENTITY_NAMES.LAB} deleted successfully`);
        fetchLabs();
      } catch (error) {
        console.error("Error deleting lab:", error);
        toast.error(`Failed to delete ${ENTITY_NAMES.LAB.toLowerCase()}`);
      }
    }
  };

  const handleMoveLab = async (labId: string, direction: "up" | "down") => {
    const labIndex = labs.findIndex((l) => l.id === labId);
    if (labIndex === -1) return;

    const targetIndex = direction === "up" ? labIndex - 1 : labIndex + 1;
    if (targetIndex < 0 || targetIndex >= labs.length) return;

    const lab = labs[labIndex];
    const targetLab = labs[targetIndex];

    try {
      // Swap order numbers
      const { error: error1 } = await supabase
        .from("labs")
        .update({ order_number: targetLab.order_number })
        .eq("id", lab.id);

      const { error: error2 } = await supabase
        .from("labs")
        .update({ order_number: lab.order_number })
        .eq("id", targetLab.id);

      if (error1 || error2) throw error1 || error2;

      fetchLabs();
    } catch (error) {
      console.error("Error reordering labs:", error);
      toast.error("Failed to reorder labs");
    }
  };

  if (isLoading) return <LoadingScreen />;

  if (!series)
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-bold mb-2">Series Not Found</h2>
        <SmartBackButton
          fallbackPath="/series"
          className="btn btn-primary"
        ></SmartBackButton>
      </div>
    );

  return (
    <>
      <div className="flex items-center mb-6">
        <SmartBackButton fallbackPath="/series" />
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="mb-1">{series.code}</h1>
              {/* <h1 className="">{series.title}</h1> */}
            </div>
            {isAdmin && (
              <div className="flex space-x-2">
                <Link to={`/series/${id}/edit`} className="btn btn-edit">
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Link>
                <button onClick={handleDeleteSeries} className="btn btn-danger">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </button>
              </div>
            )}
          </div>

          <div className="space-y-2 mb-6">
            <div className="flex items-center">
              <Tag className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="text-sm font-medium">{series.title}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                series.type === 'event'
                  ? 'bg-amber-100 text-amber-800'
                  : series.type === 'camp'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {series.type === 'event' ? 'Event' : series.type === 'camp' ? 'Camp' : 'Series'}
              </span>
            </div>
            <div className="flex items-center">
              <Clipboard className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="text-sm">{series.description}</span>
            </div>

            {series.type === 'series' && prerequisiteSeries.length > 0 && (
              <div className="flex items-center">
                <Book className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-sm">
                  Prerequisites:{" "}
                  {prerequisiteSeries.map((prereq, index) => (
                    <span key={prereq.id}>
                      <Link
                        to={`/series/${prereq.id}`}
                        className="text-link hover:text-hover"
                      >
                        {prereq.code}: {prereq.title}
                      </Link>
                      {index < prerequisiteSeries.length - 1 && ", "}
                    </span>
                  ))}
                </span>
              </div>
            )}
          </div>

          {/* {series.description && <p className="mb-8">{series.description}</p>} */}

          <div className="space-y-6">
            {(series.type === 'series' || series.type === 'camp') && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl text-main">
                  {series.type === 'camp' ? 'Days' : 'Labs'}
                </h2>
                {isAdmin && (
                  <Link
                    to={`/series/${id}/labs/new`}
                    className="btn btn-primary"
                  >
                    <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
                    Add {series.type === 'camp' ? 'Day' : ENTITY_NAMES.LAB}
                  </Link>
                )}
              </div>
              {labs.length === 0 ? (
                <p className="text-neutral-500">
                  {series.type === 'camp'
                    ? 'No days scheduled for this camp.'
                    : 'No labs found for this series.'}
                </p>
              ) : (
                <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-neutral-200">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            {series.type === 'camp' ? 'Day' : 'Lab Number'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Title
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Resources
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-neutral-200">
                        {labs.map((lab, index) => (
                          <tr key={lab.id} className="hover:bg-neutral-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                              <Link
                                to={`/series/${id}/labs/${lab.id}`}
                                className=""
                              >
                                {series.type === 'camp' ? 'Day' : 'Lab'} {lab.order_number}
                              </Link>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                              {lab.title}
                            </td>
                            <td className="px-6 py-4 text-sm text-neutral-900">
                              {lab.description || "-"}
                            </td>
                            <td className="px-6 py-4 text-sm text-neutral-900">
                              {lab.resources && lab.resources.length > 0 ? (
                                <div className="space-y-1">
                                  {lab.resources.map((resource, i) => (
                                    <a
                                      key={i}
                                      href={
                                        resource.type === "url"
                                          ? resource.url || "#"
                                          : resource.file_path || "#"
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block text-link hover:text-hover truncate"
                                    >
                                      {resource.title}
                                    </a>
                                  ))}
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 text-right">
                              <div className="flex justify-end space-x-2">
                                <Link
                                  to={`/series/${id}/labs/${lab.id}`}
                                  className="icon-view"
                                  title="View lab details"
                                >
                                  <Eye className="h-5 w-5" />
                                </Link>
                                {isAdmin && (
                                  <>
                                    <Link
                                      to={`/series/${id}/labs/${lab.id}/edit`}
                                      className="icon-edit"
                                      title="Edit lab"
                                    >
                                      <Edit className="h-5 w-5" />
                                    </Link>
                                    <button
                                      onClick={() => handleDeleteLab(lab.id)}
                                      className="icon-delete"
                                      title="Delete lab"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                    {index > 0 && (
                                      <button
                                        onClick={() =>
                                          handleMoveLab(lab.id, "up")
                                        }
                                        className="text-neutral-500 hover:text-neutral-700"
                                        title="Move up"
                                      >
                                        <ArrowUp className="h-5 w-5" />
                                      </button>
                                    )}
                                    {index < labs.length - 1 && (
                                      <button
                                        onClick={() =>
                                          handleMoveLab(lab.id, "down")
                                        }
                                        className="text-neutral-500 hover:text-neutral-700"
                                        title="Move down"
                                      >
                                        <ArrowDown className="h-5 w-5" />
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
            )}

            <RunsSection
              seriesId={id || ""}
              runs={runs}
              isAdmin={isAdmin}
              onRunCreated={handleRunsChange}
              type="current"
            />

            <RunsSection
              seriesId={id || ""}
              runs={runs}
              isAdmin={isAdmin}
              onRunCreated={handleRunsChange}
              type="past"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SeriesDetail;
